import router from '@/router';

function extractFilenameFromHeader(response) {
    const contentDisposition = response.headers['content-disposition'];
    const filenameRegex = /filename[^;=\n]*=["']?([^"';\n]*)["']?/;
    const matches = filenameRegex.exec(contentDisposition);
    if (matches != null && matches[1]) {
        return matches[1];
    }
    return 'translations.xml'; // Fallback si le nom de fichier n'est pas trouvé dans l'en-tête
}

const getFiltersUrl = (queryOrExport, forExport) => {
    const isQueryObj = queryOrExport && typeof queryOrExport === 'object';
    const q = isQueryObj ? queryOrExport : router.currentRoute.query;
    const exportMode = isQueryObj ? forExport : queryOrExport;
    let url = [];
    for (let query in q) {
        const key = query.split(':')[0];
        const operator = query.split(':')[1];
        const value = q[query];
        if (key === 'sort') {
            url.push(`sort[${value.split(':')[0]}]=${value.split(':')[1]}`);
        } else if (value && value !== '' && value !== 'null' && value !== 'undefined') {
            if (!operator) {
                if (!exportMode || (key !== 'page' && key !== 'limit')) url.push(`${key}=${encodeURIComponent(value)}`)
            } else {
                url.push(`${key}[${operator}]=${encodeURIComponent(value)}`)
            }
        }
    }
    return url.join('&')
}

const getPagination = (totalCount, current = 1, numItemsPerPage = 10) => {
    const pageRange = 5;
    const pageCount = Math.ceil(totalCount / numItemsPerPage);
    let startPage = Math.max(1, current - Math.floor(pageRange / 2));
    let endPage = Math.min(pageCount, current + Math.floor(pageRange / 2));
    if (current <= Math.floor(pageRange / 2)) {
        endPage = Math.min(pageCount, startPage + pageRange - 1);
    } else if (current > pageCount - Math.floor(pageRange / 2)) {
        startPage = Math.max(1, endPage - pageRange + 1);
    }
    const pagesInRange = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
    const currentItemCount = (current === pageCount) ? totalCount - ((current - 1) * numItemsPerPage) : numItemsPerPage;
    const firstItemNumber = ((current - 1) * numItemsPerPage) + 1;
    const lastItemNumber = firstItemNumber + currentItemCount - 1;
    return {
        last: pageCount,
        current,
        numItemsPerPage,
        first: 1,
        pageCount,
        totalCount,
        pageRange,
        startPage,
        endPage,
        next: (current < pageCount) ? current + 1 : null,
        pagesInRange,
        firstPageInRange: pagesInRange[0],
        lastPageInRange: pagesInRange[pagesInRange.length - 1],
        currentItemCount,
        firstItemNumber,
        lastItemNumber
    };
}

/**
 * Parse API Platform / Hydra 422 ConstraintViolation payloads.
 * @param {object} data - error.response.data
 * @returns {{ message: string|null, fieldErrors: Record<string, string> }}
 */
function parseApiValidationError(data) {
    const fieldErrors = {};
    if (!data || typeof data !== 'object') {
        return { message: null, fieldErrors };
    }

    const violations = data.violations;
    if (Array.isArray(violations)) {
        violations.forEach((violation) => {
            const path = violation?.propertyPath;
            const msg = violation?.message;
            if (path && msg) {
                fieldErrors[path] = msg;
            }
        });
    }

    const message =
        data['hydra:description'] ||
        data.detail ||
        (typeof data.message === 'string' ? data.message : null) ||
        null;

    if (message && typeof message === 'string') {
        message.split('\n').forEach((line) => {
            const match = line.match(/^([^:]+):\s*(.+)$/);
            if (match) {
                const path = match[1].trim();
                const msg = match[2].trim();
                if (path && msg && !fieldErrors[path]) {
                    fieldErrors[path] = msg;
                }
            }
        });
    }

    return { message, fieldErrors };
}

/**
 * Turn API violation messages (e.g. errors.email_already_used) into UI copy.
 * @param {import('vue-i18n').default} i18n
 * @param {string} message
 */
function translateApiErrorMessage(i18n, message) {
    if (!message || typeof message !== 'string') {
        return i18n.t('global.default_request_error');
    }

    if (message.includes('\n')) {
        return message
            .split('\n')
            .map((line) => translateApiErrorMessage(i18n, line))
            .join('\n');
    }

    const key = message.includes(': ')
        ? message.split(': ').pop().trim()
        : message.trim();

    if (i18n.te(key)) {
        return i18n.t(key);
    }

    return key;
}

export {
    extractFilenameFromHeader,
    getFiltersUrl,
    getPagination,
    parseApiValidationError,
    translateApiErrorMessage,
}